/**
 * <dispatch-countdown> custom element.
 *
 * Sleek, single-line same-day dispatch countdown for the product page.
 *
 * Visibility/state is driven externally (by assets/inventory.js, which
 * already fetches real per-location stock from the store's inventory
 * proxy) via the public setStockState() method, so it always reflects the
 * REAL on-hand quantity at the AlansBMX location — not just Shopify's
 * location "available" flag, which stays true for backorder/continue-
 * selling items or in-transfer stock even at zero on-hand. It also
 * updates live when the shopper switches variants, since inventory.js
 * re-checks stock on every variant change.
 *
 * States (set via setStockState):
 * - 'checking'      Stock not confirmed yet (initial state) — hidden.
 * - 'same_day'      AlansBMX has stock. Monday-Friday, before the cut-off
 *                    hour (default 1pm GMT): shows a live countdown. Same
 *                    weekday after cut-off, or on a weekend: shows a
 *                    static "next dispatch day" message instead.
 * - 'special_order'  No AlansBMX stock, but other locations have stock.
 * - 'hidden'         No stock anywhere — nothing shown.
 */
(function () {
  if (customElements.get('dispatch-countdown')) return;

  var DAY_NAMES = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];

  function pad(n) {
    return String(n).padStart(2, '0');
  }

  function renderTemplate(el, template, token, valueText, valueClass) {
    el.textContent = '';
    var parts = template.split(token);

    el.appendChild(document.createTextNode(parts[0]));

    if (parts.length > 1) {
      var span = document.createElement('span');
      span.className = valueClass;
      span.textContent = valueText;
      el.appendChild(span);
      el.appendChild(document.createTextNode(parts.slice(1).join(token)));
    }
  }

  class DispatchCountdown extends HTMLElement {
    connectedCallback() {
      var cutoffHour = parseInt(this.dataset.cutoffHour, 10);
      this.cutoffHour = isNaN(cutoffHour) ? 13 : Math.min(Math.max(cutoffHour, 0), 23);

      this.beforeText = this.dataset.beforeText || 'Order in the next {time} for dispatch today';
      this.afterText =
        this.dataset.afterText ||
        "Today's dispatch cut-off has passed. Order now for dispatch on {day}.";
      this.weekendText =
        this.dataset.weekendText ||
        "We're closed for dispatch today. Order now and we'll ship it {day}.";
      this.specialOrderText =
        this.dataset.specialOrderText ||
        "Special Order Item — not currently in shop stock, please contact us for lead times.";

      this.messageEl = this.querySelector('[data-dispatch-message]');
      this.stockState = 'checking';
      this.hidden = true;

      this.render();
      var self = this;
      this.interval = setInterval(function () {
        if (self.stockState === 'same_day') self.render();
      }, 1000);
    }

    disconnectedCallback() {
      clearInterval(this.interval);
    }

    /**
     * Called externally once real stock is known.
     * @param {'same_day'|'special_order'|'hidden'} state
     */
    setStockState(state) {
      if (state !== 'same_day' && state !== 'special_order' && state !== 'hidden') return;
      this.stockState = state;
      this.render();
    }

    nextDispatchDayName(utcDay, pastCutoffToday) {
      var offset = 1;

      if (utcDay === 6) {
        // Saturday -> Monday
        offset = 2;
      } else if (utcDay === 0) {
        // Sunday -> Monday
        offset = 1;
      } else if (utcDay === 5 && pastCutoffToday) {
        // Friday, after cut-off -> Monday
        offset = 3;
      } else {
        // Mon-Thu after cut-off -> tomorrow
        offset = 1;
      }

      return DAY_NAMES[(utcDay + offset) % 7];
    }

    formatTime(remainingMs) {
      var h = Math.floor(remainingMs / 3600000);
      var m = Math.floor((remainingMs % 3600000) / 60000);
      var s = Math.floor((remainingMs % 60000) / 1000);

      return h > 0 ? h + 'h ' + pad(m) + 'm ' + pad(s) + 's' : m + 'm ' + pad(s) + 's';
    }

    render() {
      if (!this.messageEl) return;

      if (this.stockState === 'checking' || this.stockState === 'hidden') {
        this.hidden = true;
        return;
      }

      this.hidden = false;

      if (this.stockState === 'special_order') {
        this.classList.add('dispatch-countdown--special-order');
        this.classList.remove('dispatch-countdown--closed');
        this.messageEl.textContent = this.specialOrderText;
        return;
      }

      this.classList.remove('dispatch-countdown--special-order');

      var now = new Date();
      var utcDay = now.getUTCDay(); // 0 = Sunday ... 6 = Saturday
      var nowMs = Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate(),
        now.getUTCHours(),
        now.getUTCMinutes(),
        now.getUTCSeconds()
      );
      var cutoffMs = Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate(),
        this.cutoffHour,
        0,
        0
      );

      var isWeekday = utcDay >= 1 && utcDay <= 5;
      var beforeCutoff = nowMs < cutoffMs;

      if (isWeekday && beforeCutoff) {
        this.classList.remove('dispatch-countdown--closed');
        var timeStr = this.formatTime(cutoffMs - nowMs);
        renderTemplate(this.messageEl, this.beforeText, '{time}', timeStr, 'dispatch-countdown__time');
      } else {
        this.classList.add('dispatch-countdown--closed');
        var pastCutoffToday = isWeekday && !beforeCutoff;
        var dayName = this.nextDispatchDayName(utcDay, pastCutoffToday);
        var template = isWeekday ? this.afterText : this.weekendText;
        renderTemplate(this.messageEl, template, '{day}', dayName, 'dispatch-countdown__day');
      }
    }
  }

  customElements.define('dispatch-countdown', DispatchCountdown);
})();